import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
} from "@mui/material";
import {
  IoEye,
  IoEyeOff,
  IoCheckmark,
  IoClose,
  IoMailOutline,
  IoPersonOutline,
  IoSchoolOutline,
} from "react-icons/io5";
import { School as SchoolIcon, HowToReg as HowToRegIcon } from "@mui/icons-material";
import config from "./config";
import { ROLE_COLORS } from "./constants/roles";
import RoleChip from "./components/RoleChip";

/* ── password strength helpers (same as Signup.js) ─────────────────── */
function validatePassword(password) {
  return {
    length:  password.length >= 8,
    upper:   /[A-Z]/.test(password),
    lower:   /[a-z]/.test(password),
    number:  /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  };
}

function PasswordCheck({ ok, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {ok ? (
        <IoCheckmark style={{ color: "#10a37f", fontSize: 14 }} />
      ) : (
        <IoClose style={{ color: "#dc2626", fontSize: 14 }} />
      )}
      <Typography variant="caption" sx={{ color: ok ? "#10a37f" : "#dc2626" }}>
        {label}
      </Typography>
    </Box>
  );
}

/* ── page states ─────────────────────────────────────────────────────── */
const STATE = {
  LOADING:   "loading",   // fetching token validation
  INVALID:   "invalid",   // token error (expired / cancelled / used / not found)
  FORM:      "form",      // ready to fill in username / password
  SUBMITTING:"submitting",// POST in flight
  SUCCESS:   "success",   // registration submitted
};

/* ── shared layout wrapper ───────────────────────────────────────────── */
function Layout({ children, onNavigateHome, onNavigateSignin }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        display: "flex",
        flexDirection: "column",
        fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      {/* Nav */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          backgroundColor: "white",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box
          onClick={onNavigateHome}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            color: "#1976d2",
            fontWeight: 600,
            gap: 0.5,
          }}
        >
          <SchoolIcon sx={{ fontSize: 24 }} />
          <Typography sx={{ fontWeight: 600, color: "#1976d2" }}>LearnAndMasterAI</Typography>
        </Box>
        <Box
          onClick={onNavigateSignin}
          sx={{ color: "#1976d2", cursor: "pointer", fontSize: "14px" }}
        >
          Already have an account? Sign in
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function InvitedRegister() {
  const [searchParams]    = useSearchParams();
  const navigate          = useNavigate();
  const token             = searchParams.get("token") || "";

  const [pageState,   setPageState]   = useState(STATE.LOADING);
  const [tokenError,  setTokenError]  = useState("");
  const [invitation,  setInvitation]  = useState(null);   // { invitee_email, role, school }

  const [username,    setUsername]    = useState("");
  const [password,    setPassword]    = useState("");
  const [confirm,     setConfirm]     = useState("");
  const [showPw,      setShowPw]      = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formError,   setFormError]   = useState("");

  const usernameRef = useRef(null);

  const checks   = validatePassword(password);
  const pwValid  = Object.values(checks).every(Boolean);
  const allValid = pwValid && password === confirm && username.trim().length >= 3;

  /* ── 1. Validate token on mount ──────────────────────────────────── */
  useEffect(() => {
    if (!token) {
      setTokenError("No invitation token found in the link. Please use the link from your email.");
      setPageState(STATE.INVALID);
      return;
    }

    fetch(`${config.API_BASE_URL}/api/invitations/validate/${encodeURIComponent(token)}`, {
      method: "GET",
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.valid) {
          setInvitation({
            invitee_email: data.invitee_email,
            role:          data.role,
            school:        data.school,
          });
          setPageState(STATE.FORM);
        } else {
          setTokenError(data.error || "Invalid invitation token.");
          setPageState(STATE.INVALID);
        }
      })
      .catch(() => {
        setTokenError("Could not reach the server. Please check your connection and try again.");
        setPageState(STATE.INVALID);
      });
  }, [token]);

  /* Focus username field once form is ready */
  useEffect(() => {
    if (pageState === STATE.FORM) {
      setTimeout(() => usernameRef.current?.focus(), 100);
    }
  }, [pageState]);

  /* ── 2. Submit registration ──────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    setFormError("");
    setPageState(STATE.SUBMITTING);

    try {
      const res = await fetch(`${config.API_BASE_URL}/api/invitations/register`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ token, username: username.trim(), password }),
      });

      const data = await res.json();

      if (res.ok) {
        setPageState(STATE.SUCCESS);
      } else {
        setFormError(data.detail || "Registration failed. Please try again.");
        setPageState(STATE.FORM);
      }
    } catch {
      setFormError("Network error. Please check your connection and try again.");
      setPageState(STATE.FORM);
    }
  };

  /* ── helpers ─────────────────────────────────────────────────────── */
  const getStrengthPct  = () => (Object.values(checks).filter(Boolean).length / 5) * 100;
  const getStrengthColor = () => {
    const s = getStrengthPct();
    if (s < 40) return "#dc2626";
    if (s < 80) return "#f59e0b";
    return "#10a37f";
  };
  const getStrengthText = () => {
    const s = getStrengthPct();
    if (s < 40) return "Weak";
    if (s < 80) return "Good";
    return "Strong";
  };

  const navHome   = () => navigate("/");
  const navSignin = () => navigate("/signin");

  /* ── LOADING ─────────────────────────────────────────────────────── */
  if (pageState === STATE.LOADING) {
    return (
      <Layout onNavigateHome={navHome} onNavigateSignin={navSignin}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress sx={{ color: "#1976d2", mb: 2 }} />
          <Typography color="text.secondary">Validating your invitation…</Typography>
        </Box>
      </Layout>
    );
  }

  /* ── INVALID TOKEN ───────────────────────────────────────────────── */
  if (pageState === STATE.INVALID) {
    return (
      <Layout onNavigateHome={navHome} onNavigateSignin={navSignin}>
        <Card
          sx={{
            width: "100%",
            maxWidth: 440,
            p: 4,
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#fef2f2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <IoClose style={{ color: "#dc2626", fontSize: 28 }} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Invitation Invalid
            </Typography>
            <Alert severity="error" sx={{ textAlign: "left", mb: 2 }}>
              {tokenError}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              If you believe this is an error, please contact your administrator to
              resend the invitation.
            </Typography>
            <Button variant="outlined" onClick={() => navigate("/signin")} fullWidth>
              Go to Sign In
            </Button>
          </Box>
        </Card>
      </Layout>
    );
  }

  /* ── SUCCESS ─────────────────────────────────────────────────────── */
  if (pageState === STATE.SUCCESS) {
    return (
      <Layout onNavigateHome={navHome} onNavigateSignin={navSignin}>
        <Card
          sx={{
            width: "100%",
            maxWidth: 440,
            p: 4,
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#f0fdf4",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <IoCheckmark style={{ color: "#10a37f", fontSize: 28 }} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Account Created!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your account has been set up successfully. You can sign in right now
              using the username and password you just chose.
            </Typography>
            <Alert severity="success" sx={{ mb: 3, textAlign: "left" }}>
              You're all set — no approval needed. Sign in to get started.
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate("/signin")}
              fullWidth
              sx={{ backgroundColor: "#10a37f", "&:hover": { backgroundColor: "#059669" } }}
            >
              Sign In Now
            </Button>
          </Box>
        </Card>
      </Layout>
    );
  }

  /* ── FORM (and SUBMITTING) ───────────────────────────────────────── */
  const roleColor = ROLE_COLORS[invitation?.role] || "#1976d2";

  return (
    <Layout onNavigateHome={navHome} onNavigateSignin={navSignin}>
      <Card
        sx={{
          width: "100%",
          maxWidth: 460,
          p: 4,
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: roleColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <HowToRegIcon sx={{ color: "white", fontSize: 26 }} />
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Complete Your Registration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You've been invited to join LearnAndMasterAI. Choose a username and
            password to finish setting up your account.
          </Typography>
        </Box>

        {/* Invitation summary */}
        <Box
          sx={{
            backgroundColor: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Your Invitation Details
          </Typography>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IoMailOutline style={{ color: "#6b7280", fontSize: 16 }} />
              <Typography variant="body2" color="text.secondary">
                {invitation?.invitee_email}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IoPersonOutline style={{ color: "#6b7280", fontSize: 16 }} />
              <RoleChip
                role={invitation?.role}
                sx={{ fontSize: "12px", height: 22 }}
              />
            </Box>
            {invitation?.school && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IoSchoolOutline style={{ color: "#6b7280", fontSize: 16 }} />
                <Typography variant="body2" color="text.secondary">
                  {invitation.school}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Username */}
          <TextField
            inputRef={usernameRef}
            label="Choose a username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            required
            disabled={pageState === STATE.SUBMITTING}
            inputProps={{ minLength: 3, maxLength: 50 }}
            helperText="Letters, digits, underscores, hyphens, and dots only."
          />

          {/* Password */}
          <Box>
            <TextField
              label="Choose a password"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              disabled={pageState === STATE.SUBMITTING}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPw((p) => !p)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                    >
                      {showPw ? <IoEyeOff /> : <IoEye />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {password && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Password strength
                  </Typography>
                  <Typography variant="caption" sx={{ color: getStrengthColor(), fontWeight: 600 }}>
                    {getStrengthText()}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={getStrengthPct()}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: "#e5e7eb",
                    "& .MuiLinearProgress-bar": { backgroundColor: getStrengthColor() },
                  }}
                />
                <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5 }}>
                  <PasswordCheck ok={checks.length}  label="8+ characters" />
                  <PasswordCheck ok={checks.upper}   label="Uppercase letter" />
                  <PasswordCheck ok={checks.lower}   label="Lowercase letter" />
                  <PasswordCheck ok={checks.number}  label="Number" />
                  <PasswordCheck ok={checks.special} label="Special character" />
                </Box>
              </Box>
            )}
          </Box>

          {/* Confirm password */}
          <TextField
            label="Confirm password"
            type={showConfirm ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth
            required
            disabled={pageState === STATE.SUBMITTING}
            error={confirm.length > 0 && confirm !== password}
            helperText={confirm.length > 0 && confirm !== password ? "Passwords do not match." : ""}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirm((p) => !p)}
                    edge="end"
                    size="small"
                    tabIndex={-1}
                  >
                    {showConfirm ? <IoEyeOff /> : <IoEye />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Consent line sits before the submit button — both in visual
              scan order and keyboard tab order — since this is account
              creation consent, potentially for a minor, and needs to be
              read before the commit action, not after it. Links open in a
              new tab (target="_blank") rather than navigating away, since a
              normal click here would otherwise discard the invite token and
              whatever the student has already typed into this form. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center", lineHeight: 1.5 }}
          >
            By creating an account you agree to the{" "}
            <Box
              component="a"
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: roleColor, fontWeight: 600 }}
            >
              Terms of Service
            </Box>{" "}
            and acknowledge the{" "}
            <Box
              component="a"
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: roleColor, fontWeight: 600 }}
            >
              Privacy Policy
            </Box>
            .
          </Typography>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!allValid || pageState === STATE.SUBMITTING}
            sx={{
              py: 1.25,
              backgroundColor: roleColor,
              "&:hover": { backgroundColor: roleColor, filter: "brightness(0.9)" },
              "&:disabled": { backgroundColor: "#e5e7eb" },
            }}
          >
            {pageState === STATE.SUBMITTING ? (
              <>
                <CircularProgress size={18} sx={{ color: "white", mr: 1 }} />
                Submitting…
              </>
            ) : (
              "Submit Registration"
            )}
          </Button>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", mt: 2 }}
        >
          You've already been invited, so there's no waiting — you can sign in as soon as you register.
        </Typography>
      </Card>
    </Layout>
  );
}
